import {
  Avatar,
  Dialog,
  IconButton,
  Step,
  StepContent,
  StepIconProps,
  StepLabel,
  Stepper,
  SvgIcon,
  Typography,
  TextField,
  Button,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Stack } from '@mui/system';
import CheckIcon from '@untitled-ui/icons-react/build/esm/Check';
import XIcon from '@untitled-ui/icons-react/build/esm/X';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { AccountButton } from 'src/layouts/dashboard/account-button';
import { TeamSubmitData } from 'src/pages/settings/team-information';
import { paths } from 'src/paths';
import { useDispatch, useSelector } from 'src/store';
import { thunks as teamThunks } from 'src/thunks/team';
import { TeamTableDetachedValues } from '../dashboard/team/team-table-detached';
import OnboardingCreationStep from './onboarding-creation-step';
import { MembersStep } from './onboarding-members-step';
import { ShareWithOrganizationStep } from './onboarding-share-organization-step';
import { TeamStep } from './onboarding-team-step';
import { WelcomeStep } from './onboarding-welcome-step';
import { thunks as userThunks } from 'src/thunks/user';
import { thunks as organizationThunks } from 'src/thunks/organization';
import { User } from 'src/API';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import logger from 'src/log/log';
import { useHasDefaultOrganization } from 'src/hooks/use-has-default-organization';

const StepIcon: FC<StepIconProps> = (props) => {
  const { active, completed, icon } = props;

  const highlight = active || completed;

  return (
    <Avatar
      sx={{
        height: 40,
        width: 40,
        ...(highlight && {
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
        }),
      }}
      variant="rounded"
    >
      {completed ? (
        <SvgIcon>
          <CheckIcon />
        </SvgIcon>
      ) : (
        icon
      )}
    </Avatar>
  );
};

export type CreationState = {
  teamInformation?: TeamSubmitData;
  members: TeamTableDetachedValues[];
  share: string[];
};

const OnboardingUserNameStep: FC<{ user: User; onNext: () => void }> = ({ user, onNext }) => {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: { displayName: user.displayName || '' },
    validationSchema: Yup.object({ displayName: Yup.string().max(255).required('Name is required') }),
    onSubmit: (values) => {
      dispatch(userThunks.updateUser({ id: user.id, displayName: values.displayName }));
      onNext();
    },
  });
  return (
    <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
      <Typography variant="h5" color="primary">
        Welcome! Please enter your name to get started.
      </Typography>
      <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <TextField
            label="Full Name"
            name="displayName"
            value={formik.values.displayName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(formik.touched.displayName && formik.errors.displayName)}
            helperText={formik.touched.displayName && formik.errors.displayName}
            sx={{ flexGrow: 1, minWidth: 200 }}
            autoFocus
          />
          <Button color="primary" variant="contained" type="submit">
            Save
          </Button>
        </Stack>
      </form>
    </Stack>
  );
};

export const OnboardingCreateForm: FC = () => {
  const [activeStep, setActiveStep] = useState<number>(0);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [teamState, setTeamState] = useState<CreationState>({
    teamInformation: undefined,
    members: [],
    share: [],
  });

  const currentTeam = useSelector((state) => state.team.current);
  const hasInitiallyFetched = useSelector((state) => state.team.hasInitiallyFetched);
  const [open] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [createdTeamId, setCreatedTeamId] = useState<string | null>(null);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.account);
  const hasDefaultOrganization = useHasDefaultOrganization();

  // Load all required data before rendering
  useEffect(() => {
    const loadData = async () => {
      // Load teams if not already loaded globally
      if (!hasInitiallyFetched) {
        console.log('🔄 ONBOARDING: Fetching teams (not fetched yet)');
        dispatch(teamThunks.getTeams());
      } else {
        console.log('🔄 ONBOARDING: Teams already fetched, skipping');
      }
      // Load user account data if not already loaded
      if (!user) {
        // Get current user ID from Auth and fetch user data
        const { Auth } = await import('aws-amplify');
        try {
          const currentUser = await Auth.currentAuthenticatedUser();
          const userId = currentUser.username;
          dispatch(userThunks.getAccount(userId));
        } catch (error) {
          console.error('Failed to load user account:', error);
          // Continue with onboarding even if user account loading fails
          // The user might be new and doesn't have an account record yet
        }
      }
      // Load organizations to check for default organization
      dispatch(organizationThunks.listOrganizations());

      setIsLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNext = useCallback(() => {
    setActiveStep((prevState) => prevState + 1);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prevState) => prevState - 1);
  }, []);

  const handleComplete = useCallback(() => {
    setIsComplete(true);
  }, []);

  const navigate = useNavigate();
  const handleClose = useCallback(() => {
    // Use newly created team ID if available, otherwise fall back to current team from Redux
    const teamIdToUse = createdTeamId || currentTeam?.id;
    console.log('🚀 ONBOARDING CLOSE: Navigating to team', {
      createdTeamId,
      currentTeamId: currentTeam?.id,
      teamIdToUse,
    });

    if (!teamIdToUse) {
      console.error('❌ ONBOARDING CLOSE: No team ID available for navigation');
      return;
    }
    navigate(paths.team.overview(teamIdToUse));
  }, [createdTeamId, currentTeam?.id, navigate]);

  const showUserNameStep = Boolean(user && !user.displayName);
  logger.log('user', user, 'showUserNameStep', showUserNameStep, 'hasDefaultOrganization', hasDefaultOrganization);
  const steps = useMemo(() => {
    const baseSteps = [
      {
        label: 'Welcome to Planvas',
        content: <WelcomeStep onNext={handleNext} />,
      },
      {
        label: 'The Team',
        content: (
          <TeamStep
            onBack={handleBack}
            onNext={(data) => {
              setTeamState((s) => ({ ...s, teamInformation: data }));
              handleNext();
            }}
            team={teamState.teamInformation}
          />
        ),
      },
      {
        label: 'Members',
        content: (
          <MembersStep
            users={teamState.members}
            onBack={handleBack}
            onUserChange={(users) => {
              setTeamState((s) => ({ ...s, members: users }));
            }}
            onNext={hasDefaultOrganization ? handleComplete : handleNext}
          />
        ),
      },
    ];

    // Only add the Share step if there's no default organization
    if (!hasDefaultOrganization) {
      baseSteps.push({
        label: 'Share',
        content: (
          <ShareWithOrganizationStep
            onRoleChange={(roles) => {
              setTeamState((s) => ({ ...s, share: roles }));
            }}
            roles={teamState.share}
            onBack={handleBack}
            onNext={handleComplete}
          />
        ),
      });
    }

    if (showUserNameStep && user) {
      // Insert user name step as the first step, skip Welcome
      return [
        {
          label: 'Your Name',
          content: <OnboardingUserNameStep user={user} onNext={handleNext} />,
        },
        ...baseSteps.slice(1), // skip WelcomeStep
      ];
    }
    return baseSteps;
  }, [handleBack, handleNext, handleComplete, teamState, showUserNameStep, user, hasDefaultOrganization]);

  const handleTeamCreated = useCallback((teamId: string) => {
    console.log('🎯 ONBOARDING: Team created callback received', { teamId });
    setCreatedTeamId(teamId);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen w-full">
        <CircularProgress />
      </div>
    );
  }

  if (isComplete) {
    return <OnboardingCreationStep data={teamState} onTeamCreated={handleTeamCreated} />;
  }

  return (
    <Dialog fullScreen open={open}>
      <Stack spacing={2} p={2} alignItems={'center'} justifyContent={'center'} alignContent={'center'}>
        <Stack justifyContent={'space-between'} alignItems={'center'} direction={'row'} width={'100%'}>
          {currentTeam && (
            <IconButton onClick={handleClose}>
              <SvgIcon>
                <XIcon />
              </SvgIcon>
            </IconButton>
          )}
          <AccountButton />
        </Stack>
        <Typography variant="h4" color={'primary'}>
          Create your team
        </Typography>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={{
            width: '70%',
            '& .MuiStepConnector-line': {
              borderLeftColor: 'divider',
              borderLeftWidth: 2,
              ml: 1,
            },
          }}
        >
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel StepIconComponent={StepIcon}>
                <Typography sx={{ ml: 2 }} variant="overline">
                  {step.label}
                </Typography>
              </StepLabel>
              <StepContent
                sx={{
                  borderLeftColor: 'divider',
                  borderLeftWidth: 2,
                  ml: '20px',
                  py: activeStep === index ? 4 : 2,
                }}
              >
                {step.content}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </Stack>
    </Dialog>
  );
};
